import {baseUrlForApi} from "../../client/PlaygroundSettings";
import {isSocialAuthNeedsEmailResponse} from "../../client/models/authentication/ISocialAuthNeedsEmailResponse";
import {isSocialAuthOtcSentResponse} from "../../client/models/authentication/ISocialAuthOtcSentResponse";
import {SocialSignInProvider} from "../../client/models/authentication/SocialSignInProvider";
import {socialLinkHandoff} from "./socialLinkHandoff";
import {deviceStore} from "../../redux/device/deviceStore";
import {subscribeToReceiveMessagesFromNativeApp} from "../eventBus/nativeApp/receiveMessageFromNativeAppEventBus";
import {ISocialSignInCallback} from "../eventBus/nativeApp/FromNativeAppEventTypes";
import nativeBridge from "../nativeBridge";
import playgroundApi from "../playgroundApi";
import {userAuthentication} from "../user/authentication";

export const DESKTOP_SOCIAL_SIGN_IN_TIMEOUT_MS = 600_000;

const genericSignInFailedMessage = "Sign-in failed. Please try again.";

export type DesktopSocialSignInEvent =
  | {kind: "completing"}
  | {kind: "signedIn"}
  | {kind: "needsEmail"; linkingToken: string; prefillEmail: string | null; isPrefillEmailVerified: boolean | null; provider: SocialSignInProvider}
  | {kind: "otcSent"; linkingToken: string; email: string; provider: SocialSignInProvider}
  | {kind: "error"; message: string};

type AttemptStatus = "open" | "canceled" | "expired" | "completing" | "finished";

type Attempt = {
  provider: SocialSignInProvider;
  state: string;
  verifier: string;
  status: AttemptStatus;
  timer: ReturnType<typeof setTimeout>;
};

const listeners = new Set<(event: DesktopSocialSignInEvent) => void>();
let attempt: Attempt | null = null;
let stopListening: (() => void) | null = null;

export const desktopSocialSignIn = {
  init(): () => void {
    if (stopListening !== null) {
      return stopListening;
    }

    stopListening = subscribeToReceiveMessagesFromNativeApp((message) => {
      if (message.type !== "socialSignInCallback") {
        return;
      }
      void handleCallback(message);
    });
    nativeBridge.sendMessageToNative({type: "socialSignInListenerReady"});

    return () => {
      stopListening?.();
      stopListening = null;
    };
  },

  isAvailable(): boolean {
    if (!nativeBridge.isRunningInElectron || window.socialAuthCallbackSupported !== true) {
      return false;
    }

    return deviceStore.getDeviceOsIsWindows() || deviceStore.getDeviceOsIsMac();
  },

  isAttemptOpen(): boolean {
    return attempt?.status === "open";
  },

  isCompleting(): boolean {
    return attempt?.status === "completing";
  },

  async start(provider: SocialSignInProvider): Promise<void> {
    if (attempt?.status === "completing") {
      return;
    }

    if (attempt?.status === "open") {
      desktopSocialSignIn.cancel();
    }

    const {verifier, challenge} = await createPkce();
    const state = base64Url(randomBytes(32));
    const timer = setTimeout(() => {
      if (attempt?.state === state && attempt.status === "open") {
        attempt.status = "expired";
      }
    }, DESKTOP_SOCIAL_SIGN_IN_TIMEOUT_MS);

    attempt = {provider, state, verifier, status: "open", timer};
    const url = `${baseUrlForApi}/v4/auth/${provider}/start?state=${encodeURIComponent(state)}&code_challenge=${encodeURIComponent(challenge)}`;
    nativeBridge.tellNativeToOpenUrlInBrowser(url);
  },

  cancel(): void {
    if (attempt === null || attempt.status !== "open") {
      return;
    }
    clearTimeout(attempt.timer);
    attempt.status = "canceled";
  },

  subscribe(handler: (event: DesktopSocialSignInEvent) => void): () => void {
    listeners.add(handler);
    return () => {
      listeners.delete(handler);
    };
  },
};

async function handleCallback(callback: ISocialSignInCallback): Promise<void> {
  const current = attempt;
  if (current === null || callback.state !== current.state) {
    return;
  }

  if (current.status === "canceled" || current.status === "finished" || current.status === "completing") {
    return;
  }

  if (callback.error === "cancelled") {
    clearTimeout(current.timer);
    current.status = "finished";
    return;
  }

  if (current.status === "expired" || callback.error === "failed" || callback.code === null) {
    clearTimeout(current.timer);
    current.status = "finished";
    emit({kind: "error", message: genericSignInFailedMessage});
    return;
  }

  clearTimeout(current.timer);
  current.status = "completing";
  emit({kind: "completing"});

  try {
    const result = await playgroundApi.desktopSocialAuthExchange(callback.code, current.verifier);
    if (isSocialAuthNeedsEmailResponse(result)) {
      const isPrefillEmailVerified = result.prefill_email_verified ?? null;
      socialLinkHandoff.persist({
        linkingToken: result.linking_token,
        prefillEmail: result.prefill_email,
        isPrefillEmailVerified,
        provider: current.provider,
      });
      current.status = "finished";
      emit({
        kind: "needsEmail",
        linkingToken: result.linking_token,
        prefillEmail: result.prefill_email,
        isPrefillEmailVerified,
        provider: current.provider,
      });
      return;
    }

    if (isSocialAuthOtcSentResponse(result)) {
      socialLinkHandoff.persist(socialLinkHandoff.fromOtcSent({
        linkingToken: result.linking_token,
        email: result.email,
        provider: current.provider,
      }));
      current.status = "finished";
      emit({
        kind: "otcSent",
        linkingToken: result.linking_token,
        email: result.email,
        provider: current.provider,
      });
      return;
    }

    await userAuthentication.completeSocialSignIn(result, current.provider);
    current.status = "finished";
    emit({kind: "signedIn"});
  } catch {
    current.status = "finished";
    emit({kind: "error", message: genericSignInFailedMessage});
  }
}

function emit(event: DesktopSocialSignInEvent): void {
  listeners.forEach((handler) => handler(event));
}

function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

function base64Url(bytes: Uint8Array): string {
  let binary = "";
  bytes.forEach((value) => {
    binary += String.fromCharCode(value);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function asciiBytes(value: string): Uint8Array {
  const bytes = new Uint8Array(value.length);
  for (let index = 0; index < value.length; index++) {
    bytes[index] = value.charCodeAt(index);
  }
  return bytes;
}

async function createPkce(): Promise<{verifier: string; challenge: string}> {
  const verifier = base64Url(randomBytes(32));
  const digest = await crypto.subtle.digest("SHA-256", asciiBytes(verifier));
  return {verifier, challenge: base64Url(new Uint8Array(digest))};
}
